import epsClient from 'enrollment-platform/api/eps.client'
import type {
  PlanAndRateLevelHistoryQueryDto,
  PaginatedPlanAndRateLevelHistoryResponseDto,
} from './dto/plan-and-rate-level-history.dto'
import type {
  BulkDeletePlanAndRateOverrideRulesRequestDto,
  BulkUpdatePlanAndRateStatusByIdsRequestDto,
  CheckDuplicatePlanAndRateOverrideRuleQueryDto,
  CheckDuplicatePlanAndRateOverrideRuleResponseDto,
  CreatePlanAndRateOverrideRuleRequestDto,
  DeletePlanAndRateOverrideRulesByFilterRequestDto,
  DeletePlanAndRateOverrideRulesByFilterResponseDto,
  ListPlanAndRateOverrideRulesQueryDto,
  ListPlanAndRateOverrideRulesResponseDto,
  PlanAndRateOverrideRuleResponseDto,
  UpdatePlanAndRateStatusByFilterBodyDto,
  UpdatePlanAndRateStatusByFilterQueryDto,
  UpdatePlanAndRateStatusByFilterResponseDto,
  UpdatePlanAndRateOverrideRuleRequestDto,
  UpdatePlanAndRateOverrideRuleStatusRequestDto,
  UpsertPlanAndRateFieldRequestDto,
} from './dto/plan-and-rate-override-rules.dto'

export const PLAN_AND_RATE_OVERRIDE_RULE_BASE =
  '/api/v1/plan-and-rate-override-rules'

export const listPlanAndRateOverrideRules = async (
  params: ListPlanAndRateOverrideRulesQueryDto
): Promise<ListPlanAndRateOverrideRulesResponseDto> => {
  const { data } = await epsClient.get<ListPlanAndRateOverrideRulesResponseDto>(
    PLAN_AND_RATE_OVERRIDE_RULE_BASE,
    { params }
  )
  return data
}

export const getPlanAndRateOverrideRule = async (
  id: string
): Promise<PlanAndRateOverrideRuleResponseDto> => {
  const { data } = await epsClient.get<PlanAndRateOverrideRuleResponseDto>(
    `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/${id}`
  )
  return data
}

export const createPlanAndRateOverrideRule = async (
  body: CreatePlanAndRateOverrideRuleRequestDto
): Promise<PlanAndRateOverrideRuleResponseDto> => {
  const { data } = await epsClient.post<PlanAndRateOverrideRuleResponseDto>(
    PLAN_AND_RATE_OVERRIDE_RULE_BASE,
    body
  )
  return data
}

export const updatePlanAndRateOverrideRule = async (
  id: string,
  body: UpdatePlanAndRateOverrideRuleRequestDto
): Promise<PlanAndRateOverrideRuleResponseDto> => {
  const { data } = await epsClient.patch<PlanAndRateOverrideRuleResponseDto>(
    `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/${id}`,
    body
  )
  return data
}

export const checkDuplicatePlanAndRateOverrideRule = async (
  query: CheckDuplicatePlanAndRateOverrideRuleQueryDto
): Promise<CheckDuplicatePlanAndRateOverrideRuleResponseDto> => {
  const { data } =
    await epsClient.get<CheckDuplicatePlanAndRateOverrideRuleResponseDto>(
      `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/check-duplicate`,
      { params: query }
    )
  return data
}

export const updatePlanAndRateOverrideRuleStatus = async (
  id: string,
  body: UpdatePlanAndRateOverrideRuleStatusRequestDto
): Promise<PlanAndRateOverrideRuleResponseDto> => {
  const { data } = await epsClient.patch<PlanAndRateOverrideRuleResponseDto>(
    `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/${id}/status`,
    body
  )
  return data
}

export const deletePlanAndRateOverrideRule = async (
  id: string
): Promise<void> => {
  await epsClient.delete(`${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/${id}`)
}

export const bulkDeletePlanAndRateOverrideRules = async (
  body: BulkDeletePlanAndRateOverrideRulesRequestDto
): Promise<void> => {
  await epsClient.delete(`${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/bulk`, {
    data: body,
  })
}

export const deletePlanAndRateOverrideRulesByFilter = async (
  params: DeletePlanAndRateOverrideRulesByFilterRequestDto
): Promise<DeletePlanAndRateOverrideRulesByFilterResponseDto> => {
  const { data } = await epsClient.delete(PLAN_AND_RATE_OVERRIDE_RULE_BASE, {
    params,
  })
  return data as DeletePlanAndRateOverrideRulesByFilterResponseDto
}

export const bulkUpdatePlanAndRateStatusByIds = async (
  body: BulkUpdatePlanAndRateStatusByIdsRequestDto
): Promise<void> => {
  await epsClient.patch(`${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/bulk/status`, body)
}

export const updatePlanAndRateStatusByFilter = async (
  query: UpdatePlanAndRateStatusByFilterQueryDto,
  body: UpdatePlanAndRateStatusByFilterBodyDto
): Promise<UpdatePlanAndRateStatusByFilterResponseDto> => {
  const { data } =
    await epsClient.patch<UpdatePlanAndRateStatusByFilterResponseDto>(
      `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/status`,
      body,
      { params: query }
    )
  return data
}

export const listPlanAndRateLevelHistory = async (
  params: PlanAndRateLevelHistoryQueryDto
): Promise<PaginatedPlanAndRateLevelHistoryResponseDto> => {
  const { data } =
    await epsClient.get<PaginatedPlanAndRateLevelHistoryResponseDto>(
      `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/history`,
      { params }
    )
  return data
}

export const findPlanAndRateOverrideRuleByMatch = async (params: {
  hiosPlanId: string
  year: number
  state: string
}): Promise<PlanAndRateOverrideRuleResponseDto | null> => {
  const { data } =
    await epsClient.get<PlanAndRateOverrideRuleResponseDto | null>(
      `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/find-by-match`,
      { params }
    )
  return data
}

export const upsertPlanAndRateOverrideRuleField = async (
  body: UpsertPlanAndRateFieldRequestDto
): Promise<PlanAndRateOverrideRuleResponseDto> => {
  const { data } = await epsClient.post<PlanAndRateOverrideRuleResponseDto>(
    `${PLAN_AND_RATE_OVERRIDE_RULE_BASE}/upsert-field`,
    body
  )
  return data
}
